import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert, KeyRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Brand } from "./Brand";

const RANK = { customer: 0, partner: 1, staff: 2, admin: 3, owner: 4 };
export const atLeast = (user, min) => (RANK[user?.role] ?? 0) >= RANK[min];
export const needsTwoStep = (user) => ["admin", "owner"].includes(user?.role) && !user?.session_mfa;

const Notice = ({ testid, icon: Icon, title, children }) => (
  <div data-testid={testid} className="grid min-h-screen place-items-center bg-dots px-6 text-center">
    <div className="max-w-md animate-pop">
      <Brand />
      <span className="mx-auto mt-8 grid h-14 w-14 place-items-center rounded-2xl bg-accent/15 text-accent"><Icon className="h-7 w-7" /></span>
      <h1 className="mt-5 font-heading text-2xl font-bold">{title}</h1>
      <div className="mt-3 text-sm text-muted-foreground">{children}</div>
    </div>
  </div>
);

// The /admin area under the new login: staff and up; admin and owner only with a 2-step session.
export const AdminAreaGate = ({ min = "staff", children }) => {
  const { user, sessionReady, signOut } = useAuth();
  const navigate = useNavigate();
  const from = `${window.location.pathname}${window.location.hash}`;
  if (!sessionReady) {
    return (
      <div data-testid="admin-area-checking" className="grid min-h-screen place-items-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!user || user.must_change_password) return <Navigate to="/login" replace state={{ from }} />;
  if (!atLeast(user, min)) {
    return <Notice testid="admin-area-no-role" icon={ShieldAlert} title="This page is for the Duckify team">Your account can't open the admin area.</Notice>;
  }
  if (needsTwoStep(user)) {
    const again = () => signOut(() => navigate("/login", { replace: true, state: { from } }));
    return (
      <Notice testid="admin-area-needs-2step" icon={KeyRound} title="Sign in with your 2-step code">
        <p>Admin pages open only after a sign-in with your password and your 2-step code.</p>
        <Button data-testid="admin-area-signin-again" onClick={again} className="mt-6 rounded-full px-6">Sign in again</Button>
      </Notice>
    );
  }
  return children;
};
